import { createContext, useCallback, useEffect, useState, type ReactNode } from 'react';
import { authApi } from '../services/authApi';
import type { User, LoginCredentials, SignupCredentials } from '../types';

const TOKEN_KEY = 'vybe_access_token';
const REFRESH_KEY = 'vybe_refresh_token';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  signup: (credentials: SignupCredentials) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => string | null;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getAccessToken = useCallback(() => {
    return localStorage.getItem(TOKEN_KEY);
  }, []);

  const setTokens = useCallback((accessToken: string, refreshToken: string) => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_KEY, refreshToken);
  }, []);

  const clearTokens = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    const response = await authApi.login(credentials);
    setTokens(response.access_token, response.refresh_token);
    setUser(response.user);
  }, [setTokens]);

  const signup = useCallback(async (credentials: SignupCredentials) => {
    const response = await authApi.signup(credentials);
    setTokens(response.access_token, response.refresh_token);
    setUser(response.user);
  }, [setTokens]);

  const logout = useCallback(async () => {
    const token = getAccessToken();
    if (token) {
      try {
        await authApi.logout(token);
      } catch {
        // Continue logout even if API call fails
      }
    }
    clearTokens();
    setUser(null);
  }, [getAccessToken, clearTokens]);

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = getAccessToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const userData = await authApi.getMe(token);
        setUser(userData);
      } catch {
        // Token invalid, try refresh
        const refreshToken = localStorage.getItem(REFRESH_KEY);
        if (refreshToken) {
          try {
            const response = await authApi.refreshToken(refreshToken);
            setTokens(response.access_token, response.refresh_token);
            setUser(response.user);
          } catch {
            clearTokens();
          }
        } else {
          clearTokens();
        }
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [getAccessToken, setTokens, clearTokens]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        signup,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
